package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.MtProvider
import dev.lingvo.site.ds.Radius
import kotlin.math.pow
import kotlin.math.roundToInt
import org.jetbrains.compose.web.css.px

/**
 * Each provider, in its own colour.
 *
 * Five providers used to share one grey `LBotIcon`, so the only thing telling DeepL from OpenAI on
 * a grid of five cards was the heading — and a reader scanning for the one they configured last was
 * reading, not recognising. A vendor's colour is the fastest identifier there is.
 *
 * **The vendor's own mark, not a redrawn approximation.** This used to draw a two-letter monogram
 * in the brand colour, because redrawing somebody else's trademark from memory produces a mark
 * that is subtly wrong, and a subtly wrong logo is worse than no logo. That argument no longer
 * applies: [providerGlyph] holds the vendors' actual published marks (Simple Icons' CC0 silhouettes
 * for five providers, the icon portion of MyMemory's own logo for the sixth — see
 * [ProviderGlyphs.kt][dev.lingvo.site.components.ProviderGlyph]), not a redrawing. The colours are
 * still the vendors' published brand values.
 */
@Composable
fun ProviderMark(provider: MtProvider, size: Int = 40) {
    val brand = providerBrandColor(provider)
    val glyph = providerGlyph(provider)
    val glyphSize = (size * 0.58).roundToInt()
    Box(
        Modifier
            .width(size.px)
            .height(size.px)
            .borderRadius(Radius.Control)
            // **A solid ground, not a tint of the same hue.**
            //
            // The first version drew the monogram in the brand colour on a 12% tint of that same
            // colour, which looked right and failed WCAG 1.4.3 on every provider: computed, the
            // six ranged from 2.68:1 to 4.24:1 in light mode, and DeepL's dark navy on a dark navy
            // tint over the dark surface came to 1.19:1 — a monogram nobody could read. Two shades
            // of one hue cannot separate; that is what a hue is.
            //
            // A solid brand ground with the foreground chosen by contrast is also what an app icon
            // actually looks like. Worst case is now 4.88:1.
            .backgroundColor(brand)
            // **Decorative, and announced as nothing.** The glyph carries no text a screen reader
            // could read, but it still shouldn't be traversed as an image — the vendor's name is
            // already the heading immediately beside it, so without this a provider card announces
            // an unlabelled graphic ahead of a heading that already names the vendor. `LProjectMark`
            // hides its own mark for the same reason.
            .attr("aria-hidden", "true"),
        contentAlignment = Alignment.Center,
    ) {
        Svg(attrs = Modifier.width(glyphSize.px).height(glyphSize.px).toAttrs {
            viewBox(ViewBox.sized(glyph.viewBox, glyph.viewBox))
            attr("aria-hidden", "true")
            attr("focusable", "false")
            attr("data-provider-glyph", provider.id)
        }) {
            glyph.paths.forEach { pathData ->
                Path {
                    d(pathData)
                    attr("fill", providerMarkForeground(provider).toCssRgb())
                }
            }
        }
    }
}

/**
 * The vendor's published brand colour.
 *
 * Used for the glyph and its ground, and for nothing that carries meaning — readiness is the
 * badge's job, and a reader who cannot tell these hues apart loses nothing but recognition speed.
 */
internal fun providerBrandColor(provider: MtProvider): Color = when (provider) {
    MtProvider.CLAUDE -> Color.rgb(0xD9, 0x77, 0x57)
    MtProvider.OPENAI -> Color.rgb(0x10, 0xA3, 0x7F)
    MtProvider.DEEPL -> Color.rgb(0x0F, 0x2B, 0x46)
    MtProvider.GOOGLE -> Color.rgb(0x42, 0x85, 0xF4)
    MtProvider.LIBRETRANSLATE -> Color.rgb(0x2E, 0x7D, 0x32)
    // The blue from MyMemory's own published logo, not an invented brand colour: the wordmark
    // beside its circular mark is set in this exact blue, so it is what the vendor actually ships.
    MtProvider.MYMEMORY -> Color.rgb(0x00, 0x57, 0xAA)
}

/** A CSS `rgb()` string for an `attr("fill", …)` on raw SVG, which does not accept a [Color]. */
private fun Color.toCssRgb(): String {
    val rgb = toRgb()
    return "rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})"
}

/**
 * Whichever of white or ink reads on this ground, by measured contrast rather than by eye.
 *
 * The naive rule — light text on a dark colour — gets Claude's terracotta and Google's blue wrong:
 * both are mid-luminance, and white on them is 3.1:1 and 3.6:1 where ink is 5.6:1 and 4.9:1.
 * Computing the ratio is the only way to be right about the middle of the range, which is where
 * brand colours cluster.
 */
internal fun providerMarkForeground(provider: MtProvider): Color = readableOn(providerBrandColor(provider))

private fun readableOn(ground: Color): Color =
    if (contrastRatio(Color.rgb(0xFF, 0xFF, 0xFF), ground) >= contrastRatio(PROVIDER_INK, ground)) {
        Color.rgb(0xFF, 0xFF, 0xFF)
    } else {
        PROVIDER_INK
    }

/** WCAG 2.1 relative luminance and contrast, so the choice above is measured and not guessed. */
private fun contrastRatio(a: Color, b: Color): Double {
    val la = relativeLuminance(a)
    val lb = relativeLuminance(b)
    return (maxOf(la, lb) + 0.05) / (minOf(la, lb) + 0.05)
}

private fun relativeLuminance(color: Color): Double {
    val rgb = color.toRgb()
    fun channel(value: Int): Double {
        val c = value / 255.0
        return if (c <= 0.03928) c / 12.92 else ((c + 0.055) / 1.055).pow(2.4)
    }
    return 0.2126 * channel(rgb.red) + 0.7152 * channel(rgb.green) + 0.0722 * channel(rgb.blue)
}

/** The dark surface, reused as ink so a light tile's glyph matches the product's own black. */
private val PROVIDER_INK = Color.rgb(0x1C, 0x1A, 0x16)
